import { InfoBox } from '@common/components';
import { OptionSelector } from '@common/components/OptionSelector';
import { useTranslations } from '@lang';
import { FC, useEffect, useRef } from 'react';
import {
  Controller,
  FieldArrayPath,
  get,
  Path,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import type { WrappedFieldProps } from 'redux-form';
import { ConfigurationGroupOptionType } from 'shared-types/SpaPackage';
import { SpaPackageFormValues } from '../../../../../SpaPackageForm.schema';
import { GroupError } from '../GroupError';
import { ConfigurationGroupHeading } from './ConfigurationGroupHeading';
import { ConfigurationGroupWrapper } from './ConfigurationGroupHeading.styles';

type SelectionData = {
  configurationGroupId: number;
  optionId: number;
  isSelected: boolean;
  timeslot: { startTime: string; endTime: string } | null;
};

type ConfigurationGroupV2Props = {
  fieldName: string;
  options: ConfigurationGroupOptionType[];
  minimumSelections?: number;
  maximumSelections?: number;
  supportsSelections?: boolean;
  requireTimeSlots: boolean;
  configurationGroupId: number;
  index: number;
  supportsTimeslots?: boolean;
};

export const ConfigurationGroupV2: FC<ConfigurationGroupV2Props> = ({
  fieldName,
  options,
  minimumSelections,
  maximumSelections,
  supportsSelections,
  requireTimeSlots,
  configurationGroupId,
  index,
  supportsTimeslots: _supportsTimeslots,
}) => {
  const { t } = useTranslations();
  const { control, formState, trigger } = useFormContext<SpaPackageFormValues>();
  const { fields, append } = useFieldArray({
    control,
    name: fieldName as FieldArrayPath<SpaPackageFormValues>,
  });
  const liveFieldValues = useWatch({ control, name: fieldName as Path<SpaPackageFormValues> }) as
    | SelectionData[]
    | undefined;
  const hasOptionTimeslots = options.some((o) => !!o.timeslots?.length);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && fields.length === 0 && options.length > 0) {
      let didAppend = false;
      options.forEach(({ id, timeslots }) => {
        if (!supportsSelections && !timeslots) {
          return;
        }
        append({
          configurationGroupId,
          optionId: id,
          isSelected: false,
          timeslot: null,
        } as SelectionData);
        didAppend = true;
      });
      // Only lock initialization if fields were actually appended —
      // if all options were skipped (no timeslots yet), allow re-run when timeslots arrive
      initialized.current = didAppend;
      if (didAppend) {
        trigger();
      }
    }
  }, [options.length, hasOptionTimeslots]);

  const numberSelectedOptions = (liveFieldValues || []).filter((f) => f?.isSelected).length;

  const groupError =
    formState.submitCount > 0 ? get(formState.errors, fieldName)?.message : undefined;

  return (
    <ConfigurationGroupWrapper>
      <ConfigurationGroupHeading
        minimumSelections={minimumSelections}
        maximumSelections={maximumSelections}
        numberSelected={numberSelectedOptions}
        className={index > 0 ? 'with-top-margin' : ''}
      />
      {requireTimeSlots && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      <GroupError meta={{ submitFailed: formState.submitCount > 0, error: groupError }} />
      {fields.map((fieldEntry, fieldIndex) => {
        const currentField = fieldEntry as SelectionData & { id: string };
        const { optionId } = currentField;
        const { name, timeslots } = options.find((o) => o.id === optionId) || {};

        const liveValue = liveFieldValues?.[fieldIndex];
        const isFieldSelected = liveValue?.isSelected ?? false;

        let disabled = maximumSelections
          ? numberSelectedOptions >= maximumSelections && !isFieldSelected
          : false;

        if (timeslots?.length === 0) {
          disabled = true;
        }

        return (
          <Controller
            key={fieldEntry.id}
            name={`${fieldName}.${fieldIndex}` as Path<SpaPackageFormValues>}
            control={control}
            render={({ field, fieldState }) => (
              <OptionSelector
                input={
                  {
                    value: field.value,
                    onChange: field.onChange,
                  } as unknown as WrappedFieldProps['input']
                }
                meta={
                  {
                    submitFailed: formState.submitCount > 0,
                    error: fieldState.error?.message,
                  } as unknown as WrappedFieldProps['meta']
                }
                optionTitle={name}
                supportsSelections={supportsSelections}
                disabled={disabled}
                timeslots={timeslots}
                requireTimeSlots={requireTimeSlots}
              />
            )}
          />
        );
      })}
    </ConfigurationGroupWrapper>
  );
};
